import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, User, MapPin, Briefcase, GraduationCap, Wallet, 
  Target, TrendingUp, Award, Gift, ChevronRight, Loader2,
  Calendar, PiggyBank, Building, Heart, Lightbulb, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function InvestorProfile() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState(null);
  const [profile, setProfile] = useState({});
  const [completion, setCompletion] = useState(0);
  const [rewards, setRewards] = useState([]);
  const [activeTab, setActiveTab] = useState('demographics');

  const fetchOptions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/investor-profile/options`);
      setOptions(response.data);
    } catch (error) {
      console.error('Failed to fetch options:', error);
    }
  }, []);

  const fetchProfile = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/investor-profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data.profile || {});
      setCompletion(response.data.completion_percentage || 0);
      setRewards(response.data.rewards_earned || []);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchOptions();
    fetchProfile();
  }, [fetchOptions, fetchProfile]);

  const handleSave = async (fieldsToSave) => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/api/investor-profile`, fieldsToSave, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProfile(response.data.profile);
      setCompletion(response.data.completion_percentage);
      
      if (response.data.new_rewards?.length > 0) {
        response.data.new_rewards.forEach(reward => {
          toast.success(
            <div className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-amber-500" />
              <span>{reward.label}</span>
            </div>
          );
        });
        setRewards(prev => [...prev, ...response.data.new_rewards]);
      } else {
        toast.success(t('Profile updated!', 'Wasifu umesasishwa!'));
      }
    } catch (error) {
      toast.error(t('Failed to save', 'Imeshindwa kuhifadhi'));
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const updateArrayField = (field, value, checked) => {
    setProfile(prev => {
      const current = prev[field] || [];
      if (checked) {
        return { ...prev, [field]: [...current, value] };
      } else {
        return { ...prev, [field]: current.filter(v => v !== value) };
      }
    });
  };

  const getLabel = (item) => language === 'sw' ? item.label_sw : item.label_en;

  if (loading || !options) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-enter space-y-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="profile-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <User className="w-6 h-6" />
              {t('Investor Profile', 'Wasifu wa Mwekezaji')}
            </h1>
            <p className="text-muted-foreground text-sm">
              {t('Help us personalize your experience', 'Tusaidie kukupa uzoefu bora zaidi')}
            </p>
          </div>
        </div>

        {/* Completion Progress */}
        <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-emerald-800">
                {t('Profile Completion', 'Ukamilifu wa Wasifu')}
              </span>
              <Badge className="bg-emerald-500">{completion}%</Badge>
            </div>
            <Progress value={completion} className="h-2 bg-emerald-100" />
            <p className="text-xs text-emerald-700 mt-2">
              {completion < 50 
                ? t('Complete your profile to unlock personalized features!', 'Kamilisha wasifu wako kupata vipengele maalum!')
                : completion < 100 
                ? t('Great progress! A few more fields to go.', 'Maendeleo mazuri! Sehemu chache zimebaki.')
                : t('Profile complete! Enjoy all features.', 'Wasifu kamili! Furahia vipengele vyote.')}
            </p>
          </CardContent>
        </Card>

        {/* Rewards Earned */}
        {rewards.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-500" />
                {t('Rewards Earned', 'Zawadi Ulizoipata')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {rewards.map((reward, idx) => (
                  <Badge key={idx} variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                    <Gift className="w-3 h-3 mr-1" />
                    {reward.label}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Profile Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="demographics" data-testid="tab-demographics">
              <User className="w-4 h-4 mr-1 hidden sm:inline" />
              {t('About You', 'Kuhusu Wewe')}
            </TabsTrigger>
            <TabsTrigger value="financial" data-testid="tab-financial">
              <Wallet className="w-4 h-4 mr-1 hidden sm:inline" />
              {t('Finances', 'Fedha')}
            </TabsTrigger>
            <TabsTrigger value="preferences" data-testid="tab-preferences">
              <Target className="w-4 h-4 mr-1 hidden sm:inline" />
              {t('Goals', 'Malengo')}
            </TabsTrigger>
            <TabsTrigger value="experience" data-testid="tab-experience">
              <TrendingUp className="w-4 h-4 mr-1 hidden sm:inline" />
              {t('Experience', 'Uzoefu')}
            </TabsTrigger>
          </TabsList>

          {/* Demographics Tab */}
          <TabsContent value="demographics">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5 text-primary" />
                  {t('Personal Information', 'Taarifa Binafsi')}
                </CardTitle>
                <CardDescription>
                  {t('Help us understand our investor community better', 'Tusaidie kuelewa jamii yetu ya wawekezaji vizuri')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Date of Birth */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {t('Date of Birth', 'Tarehe ya Kuzaliwa')}
                    </Label>
                    <Input
                      type="date"
                      value={profile.date_of_birth || ''}
                      onChange={(e) => updateField('date_of_birth', e.target.value)}
                      data-testid="input-dob"
                    />
                    <p className="text-xs text-muted-foreground">
                      {t('Unlock age-specific investment tips', 'Pata ushauri wa uwekezaji kwa umri wako')}
                    </p>
                  </div>

                  {/* Gender */}
                  <div className="space-y-2">
                    <Label>{t('Gender', 'Jinsia')}</Label>
                    <Select value={profile.gender || ''} onValueChange={(v) => updateField('gender', v)}>
                      <SelectTrigger data-testid="select-gender">
                        <SelectValue placeholder={t('Select...', 'Chagua...')} />
                      </SelectTrigger>
                      <SelectContent>
                        {options.genders.map(g => (
                          <SelectItem key={g.value} value={g.value}>{getLabel(g)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Region */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      {t('Region in Tanzania', 'Mkoa Tanzania')}
                    </Label>
                    <Select value={profile.region || ''} onValueChange={(v) => updateField('region', v)}>
                      <SelectTrigger data-testid="select-region">
                        <SelectValue placeholder={t('Select region...', 'Chagua mkoa...')} />
                      </SelectTrigger>
                      <SelectContent>
                        {options.regions.map(r => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {t('Earn "Local Investor" badge', 'Pata beji ya "Mwekezaji wa Hapa"')}
                    </p>
                  </div>

                  {/* Education */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <GraduationCap className="w-4 h-4" />
                      {t('Education Level', 'Kiwango cha Elimu')}
                    </Label>
                    <Select value={profile.education_level || ''} onValueChange={(v) => updateField('education_level', v)}>
                      <SelectTrigger data-testid="select-education">
                        <SelectValue placeholder={t('Select...', 'Chagua...')} />
                      </SelectTrigger>
                      <SelectContent>
                        {options.education_levels.map(e => (
                          <SelectItem key={e.value} value={e.value}>{getLabel(e)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Employment */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4" />
                      {t('Employment Status', 'Hali ya Ajira')}
                    </Label>
                    <Select value={profile.employment_status || ''} onValueChange={(v) => updateField('employment_status', v)}>
                      <SelectTrigger data-testid="select-employment">
                        <SelectValue placeholder={t('Select...', 'Chagua...')} />
                      </SelectTrigger>
                      <SelectContent>
                        {options.employment_status.map(e => (
                          <SelectItem key={e.value} value={e.value}>{getLabel(e)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Occupation */}
                  <div className="space-y-2">
                    <Label>{t('Occupation/Industry', 'Kazi/Sekta')}</Label>
                    <Input
                      placeholder={t('e.g. Teacher, Engineer...', 'mfano Mwalimu, Mhandisi...')}
                      value={profile.occupation || ''}
                      onChange={(e) => updateField('occupation', e.target.value)}
                      data-testid="input-occupation"
                    />
                  </div>
                </div>

                <Button 
                  onClick={() => handleSave({
                    date_of_birth: profile.date_of_birth,
                    gender: profile.gender,
                    region: profile.region,
                    education_level: profile.education_level,
                    employment_status: profile.employment_status,
                    occupation: profile.occupation
                  })}
                  disabled={saving}
                  className="w-full sm:w-auto"
                  data-testid="save-demographics-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {t('Save Demographics', 'Hifadhi Taarifa')}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Financial Tab */}
          <TabsContent value="financial">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-primary" />
                  {t('Financial Profile', 'Wasifu wa Kifedha')}
                </CardTitle>
                <CardDescription>
                  {t('This helps us provide relevant investment recommendations', 'Hii inatusaidia kukupa mapendekezo sahihi ya uwekezaji')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Income Range */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Wallet className="w-4 h-4" />
                      {t('Monthly Income Range', 'Kipato cha Kila Mwezi')}
                    </Label>
                    <Select value={profile.income_range || ''} onValueChange={(v) => updateField('income_range', v)}>
                      <SelectTrigger data-testid="select-income">
                        <SelectValue placeholder={t('Select range...', 'Chagua kiwango...')} />
                      </SelectTrigger>
                      <SelectContent>
                        {options.income_ranges.map(i => (
                          <SelectItem key={i.value} value={i.value}>{getLabel(i)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {t('Unlock personalized simulator settings', 'Pata mipangilio maalum ya kihesabu')}
                    </p>
                  </div>

                  {/* Savings Capacity */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <PiggyBank className="w-4 h-4" />
                      {t('Monthly Savings Capacity', 'Uwezo wa Kuokoa Kila Mwezi')}
                    </Label>
                    <Select value={profile.monthly_savings_capacity || ''} onValueChange={(v) => updateField('monthly_savings_capacity', v)}>
                      <SelectTrigger data-testid="select-savings">
                        <SelectValue placeholder={t('Select...', 'Chagua...')} />
                      </SelectTrigger>
                      <SelectContent>
                        {options.savings_capacity.map(s => (
                          <SelectItem key={s.value} value={s.value}>{getLabel(s)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {t('Unlock savings strategies module', 'Pata moduli ya mikakati ya kuokoa')}
                    </p>
                  </div>

                  {/* Investment Ability */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      {t('Monthly Investment Ability', 'Uwezo wa Kuwekeza Kila Mwezi')}
                    </Label>
                    <Select value={profile.monthly_investment_ability || ''} onValueChange={(v) => updateField('monthly_investment_ability', v)}>
                      <SelectTrigger data-testid="select-investment-ability">
                        <SelectValue placeholder={t('Select...', 'Chagua...')} />
                      </SelectTrigger>
                      <SelectContent>
                        {options.savings_capacity.map(s => (
                          <SelectItem key={s.value} value={s.value}>{getLabel(s)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Existing Investments */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    {t('Current Investments (if any)', 'Uwekezaji Uliopo (kama upo)')}
                  </Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {options.asset_types.map(asset => (
                      <div key={asset.value} className="flex items-center space-x-2">
                        <Checkbox
                          id={`existing-${asset.value}`}
                          checked={(profile.existing_investments || []).includes(asset.value)}
                          onCheckedChange={(checked) => updateArrayField('existing_investments', asset.value, checked)}
                        />
                        <label htmlFor={`existing-${asset.value}`} className="text-sm cursor-pointer">
                          {getLabel(asset)}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <Button 
                  onClick={() => handleSave({
                    income_range: profile.income_range,
                    monthly_savings_capacity: profile.monthly_savings_capacity,
                    monthly_investment_ability: profile.monthly_investment_ability,
                    existing_investments: profile.existing_investments
                  })}
                  disabled={saving}
                  className="w-full sm:w-auto"
                  data-testid="save-financial-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {t('Save Financial Info', 'Hifadhi Taarifa za Fedha')}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Preferences Tab */}
          <TabsContent value="preferences">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-primary" />
                  {t('Investment Preferences', 'Mapendeleo ya Uwekezaji')}
                </CardTitle>
                <CardDescription>
                  {t('What are your investment goals and preferred assets?', 'Malengo yako ya uwekezaji na mali unayopendelea ni yapi?')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Investment Horizon */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {t('Investment Time Horizon', 'Muda wa Uwekezaji')}
                  </Label>
                  <Select value={profile.investment_horizon || ''} onValueChange={(v) => updateField('investment_horizon', v)}>
                    <SelectTrigger data-testid="select-horizon">
                      <SelectValue placeholder={t('How long do you plan to invest?', 'Unapanga kuwekeza kwa muda gani?')} />
                    </SelectTrigger>
                    <SelectContent>
                      {options.investment_horizons.map(h => (
                        <SelectItem key={h.value} value={h.value}>{getLabel(h)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {t('Unlock AI goal recommendations', 'Pata mapendekezo ya AI ya malengo')}
                  </p>
                </div>

                {/* Preferred Asset Types */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Heart className="w-4 h-4" />
                    {t('Preferred Investment Types', 'Aina za Uwekezaji Unazopendelea')}
                  </Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {options.asset_types.map(asset => (
                      <div key={asset.value} className="flex items-center space-x-2">
                        <Checkbox
                          id={`pref-${asset.value}`}
                          checked={(profile.preferred_asset_types || []).includes(asset.value)}
                          onCheckedChange={(checked) => updateArrayField('preferred_asset_types', asset.value, checked)}
                        />
                        <label htmlFor={`pref-${asset.value}`} className="text-sm cursor-pointer">
                          {getLabel(asset)}
                        </label>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t('Unlock asset deep-dive content', 'Pata maudhui ya kina ya mali')}
                  </p>
                </div>

                {/* Investment Goals */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    {t('Investment Goals', 'Malengo ya Uwekezaji')}
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {options.investment_goals.map(goal => (
                      <div key={goal.value} className="flex items-center space-x-2">
                        <Checkbox
                          id={`goal-${goal.value}`}
                          checked={(profile.investment_goals || []).includes(goal.value)}
                          onCheckedChange={(checked) => updateArrayField('investment_goals', goal.value, checked)}
                        />
                        <label htmlFor={`goal-${goal.value}`} className="text-sm cursor-pointer">
                          {getLabel(goal)}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <Button 
                  onClick={() => handleSave({
                    investment_horizon: profile.investment_horizon,
                    preferred_asset_types: profile.preferred_asset_types,
                    investment_goals: profile.investment_goals
                  })}
                  disabled={saving}
                  className="w-full sm:w-auto"
                  data-testid="save-preferences-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {t('Save Preferences', 'Hifadhi Mapendeleo')}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Experience Tab */}
          <TabsContent value="experience">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  {t('Investment Experience', 'Uzoefu wa Uwekezaji')}
                </CardTitle>
                <CardDescription>
                  {t('Tell us about your investing background', 'Tuambie kuhusu historia yako ya uwekezaji')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Experience Level */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Lightbulb className="w-4 h-4" />
                      {t('Investing Experience', 'Uzoefu wa Uwekezaji')}
                    </Label>
                    <Select value={profile.investing_experience || ''} onValueChange={(v) => updateField('investing_experience', v)}>
                      <SelectTrigger data-testid="select-experience">
                        <SelectValue placeholder={t('Select your level...', 'Chagua kiwango chako...')} />
                      </SelectTrigger>
                      <SelectContent>
                        {options.experience_levels.map(e => (
                          <SelectItem key={e.value} value={e.value}>{getLabel(e)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* DSE Familiarity */}
                  <div className="space-y-2">
                    <Label>{t('DSE Familiarity', 'Ujuzi wa DSE')}</Label>
                    <Select value={profile.dse_familiarity || ''} onValueChange={(v) => updateField('dse_familiarity', v)}>
                      <SelectTrigger data-testid="select-dse-familiarity">
                        <SelectValue placeholder={t('How familiar are you with DSE?', 'Unaijua DSE kiasi gani?')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="never_heard">{t('Never heard of it', 'Sijawahi kusikia')}</SelectItem>
                        <SelectItem value="heard_not_used">{t('Heard of it, never invested', 'Nimesikia, sijawahi kuwekeza')}</SelectItem>
                        <SelectItem value="researching">{t('Currently researching', 'Ninafanya utafiti sasa')}</SelectItem>
                        <SelectItem value="invested">{t('Have invested before', 'Nimeshawekeza hapo awali')}</SelectItem>
                        <SelectItem value="active">{t('Active investor', 'Mwekezaji hai')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Broker & CDS Account */}
                <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                  <h4 className="font-medium">{t('Account Status', 'Hali ya Akaunti')}</h4>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="has-broker"
                        checked={profile.has_broker_account || false}
                        onCheckedChange={(checked) => updateField('has_broker_account', checked)}
                      />
                      <label htmlFor="has-broker" className="text-sm cursor-pointer">
                        {t('I have a broker account', 'Nina akaunti ya dalali')}
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="has-cds"
                        checked={profile.has_cds_account || false}
                        onCheckedChange={(checked) => updateField('has_cds_account', checked)}
                      />
                      <label htmlFor="has-cds" className="text-sm cursor-pointer">
                        {t('I have a CDS account', 'Nina akaunti ya CDS')}
                      </label>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t('CDS (Central Depository System) account is required to trade on DSE', 'Akaunti ya CDS inahitajika kufanya biashara DSE')}
                  </p>
                </div>

                <Button 
                  onClick={() => handleSave({
                    investing_experience: profile.investing_experience,
                    dse_familiarity: profile.dse_familiarity,
                    has_broker_account: profile.has_broker_account,
                    has_cds_account: profile.has_cds_account
                  })}
                  disabled={saving}
                  className="w-full sm:w-auto"
                  data-testid="save-experience-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {t('Save Experience', 'Hifadhi Uzoefu')}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Why This Matters */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <h3 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
              <Lightbulb className="w-5 h-5" />
              {t('Why We Ask', 'Kwa Nini Tunauliza')}
            </h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                {t('Personalize your learning path and recommendations', 'Kubinafsisha njia yako ya kujifunza na mapendekezo')}
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                {t('Help build Tanzania\'s first comprehensive investor database', 'Kusaidia kujenga hifadhidata ya kwanza kamili ya wawekezaji Tanzania')}
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                {t('Unlock features and content tailored to you', 'Kufungua vipengele na maudhui yaliyotengenezwa kwa ajili yako')}
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                {t('Your data is anonymous and secure', 'Taarifa zako ni za siri na salama')}
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
